import React, { useState, useEffect } from 'react';
import {
  Table,
  Tag,
  Space,
  Button,
  Modal,
  Input,
  Skeleton,
  Radio,
  Tooltip,
  Flex,
  message,
  Select,
} from 'antd';
import { getSoloEvents, getGroupEvents } from '../../api/event';
import { changeApplicationEvent } from '../../api/application';
import {
  DeleteOutlined,
  SearchOutlined,
  EditOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { loadState } from '../../stores/store';
import GroupMembersModal from './groupMembersModal';

const { confirm } = Modal;

const ApplicationTable = ({
  loading,
  applications,
  isAdmin,
  onDelete,
  onApplicationUpdate,
}) => {
  const [searchText, setSearchText] = useState('');
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [paidFilter, setPaidFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const [modalVisible, setModalVisible] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupName, setGroupName] = useState('');

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [currentEditingApplication, setCurrentEditingApplication] = useState(null);
  const [eventOptions, setEventOptions] = useState([]);
  const [isEventOptionsLoading, setIsEventOptionsLoading] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const state = loadState();

  useEffect(() => {
    const filtered = applications.filter((application) => {
      const eventMatch =
        application.event &&
        (application.event.name.toLowerCase().includes(searchText.toLowerCase()) ||
          application.event.style.toLowerCase().includes(searchText.toLowerCase()));

      const contestantMatch =
        application.contestant &&
        `${application.contestant.first_name} ${application.contestant.last_name}`
          .toLowerCase()
          .includes(searchText.toLowerCase());

      const groupMatch =
        application.group &&
        application.group.group_name.toLowerCase().includes(searchText.toLowerCase());

      const paidMatch =
        paidFilter === 'all' ||
        (paidFilter === 'paid' && application.paid) ||
        (paidFilter === 'notPaid' && !application.paid);

      return (eventMatch || contestantMatch || groupMatch) && paidMatch;
    });

    setFilteredApplications(filtered);
  }, [searchText, applications, paidFilter]);

  const handleEditApplicationEvent = async (application) => {
    setCurrentEditingApplication(application);
    setEditModalVisible(true);
    setEventOptions([]);
    setSelectedEventId(null);
    setIsEventOptionsLoading(true);

    try {
      let events = [];

      if (application.contestant) {
        events = await getSoloEvents();
      } else if (application.group) {
        events = await getGroupEvents();
      }

      setEventOptions(events);
    } catch (error) {
      console.error('Error fetching events:', error);
      message.error('Error fetching events');
    } finally {
      setIsEventOptionsLoading(false);
    }
  };

  const handleChangeEvent = async () => {
    if (!selectedEventId || !currentEditingApplication) {
      return;
    }

    setConfirmLoading(true);

    try {
      await changeApplicationEvent(currentEditingApplication._id, selectedEventId);
      message.success('Application event changed successfully');

      if (onApplicationUpdate) {
        onApplicationUpdate();
      }

      setEditModalVisible(false);
      setCurrentEditingApplication(null);
      setSelectedEventId(null);
    } catch (error) {
      message.error('Failed to change application event');
      console.error('Error changing application event:', error);
    } finally {
      setConfirmLoading(false);
    }
  };

  const clearSearch = () => {
    setSearchText('');
  };

  const showDeleteConfirm = (applicationId) => {
    confirm({
      title: 'Are you sure you want to delete this application?',
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        onDelete(applicationId);
      },
      onCancel() {},
    });
  };

  const openGroupMembersModal = (groupId, group) => {
    setGroupMembers(group.group);
    setGroupName(group.group_name);
    setModalVisible(true);
  };

  const closeGroupMembersModal = () => {
    setModalVisible(false);
    setGroupMembers([]);
    setGroupName('');
  };

  const columns = [
    {
      title: 'Event',
      dataIndex: 'event',
      key: 'event',
      render: (event, application) =>
        event
          ? `${event.name} - ${event.style} ${
              application.instrument ? '(' + application.instrument + ')' : ''
            }`
          : 'No Event',
    },
    {
      title: 'Age Group',
      dataIndex: 'group_age',
      key: 'group_age',
    },
    {
      title: 'Solo/Group',
      dataIndex: 'contestant',
      key: 'contestant',
      filters: [
        { text: 'Contestant', value: 'contestant' },
        { text: 'Group', value: 'group' },
      ],
      onFilter: (value, record) => {
        if (value === 'contestant') {
          return record.contestant !== null;
        } else if (value === 'group') {
          return record.contestant === null;
        }
        return false;
      },
      render: (contestant, row) => {
        if (contestant) {
          return (
            <span>
              <Space>
                {loadState().user.role !== 'contestant' && (
                  <span>
                    {contestant.first_name} {contestant.last_name}
                  </span>
                )}
                <Tag color="green">Solo</Tag>
              </Space>
            </span>
          );
        } else if (row.group) {
          return (
            <a
              onClick={(e) => {
                e.stopPropagation();
                openGroupMembersModal(row.key, row.group);
              }}
            >
              <Space>
                {row.group.group_name}
                <Tag color="blue">Group</Tag>
              </Space>
            </a>
          );
        }
        return null;
      },
    },
    {
      title: 'Paid',
      dataIndex: 'paid',
      key: 'paid',
      render: (paid, record) => (
        <Space>
          <Tag color={record.passed && !paid ? 'blue' : paid ? 'green' : 'red'}>
            {record.passed && !paid
              ? 'Sponsored'
              : paid
              ? 'Paid'
              : 'Not Paid'}
          </Tag>
          {!paid && state.user.role === 'contestant' ? (
            <a href="/invoice" style={{ marginLeft: '8px' }}>
              <Button type="link">Pay Here</Button>
            </a>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <Space>
          {isAdmin && (
            <Tooltip title="Change Event">
              <Button
                icon={<EditOutlined />}
                type="text"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditApplicationEvent(record);
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Delete Application">
            <Button
              danger
              icon={<DeleteOutlined />}
              type="text"
              onClick={(e) => {
                e.stopPropagation();
                showDeleteConfirm(record._id);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Flex
        justify="space-between"
        className="dashboard_table_filters"
        style={{ marginBottom: 16 }}
      >
        <Input
          style={{ marginRight: '8px' }}
          placeholder="Search by event name, event style, or solo/group name"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          suffix={
            <>
              <SearchOutlined style={{ marginRight: '8px' }} />
              {searchText && (
                <CloseOutlined
                  onClick={clearSearch}
                  style={{ cursor: 'pointer' }}
                />
              )}
            </>
          }
          className="dashboard_table_filters_search"
        />
        <Radio.Group
          onChange={(e) => setPaidFilter(e.target.value)}
          value={paidFilter}
          style={{ display: 'flex' }}
        >
          <Radio.Button
            value="all"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            All
          </Radio.Button>
          <Radio.Button
            value="paid"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Paid
          </Radio.Button>
          <Radio.Button
            value="notPaid"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Not Paid
          </Radio.Button>
        </Radio.Group>
      </Flex>

      {loading ? (
        <Skeleton active />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredApplications.map((application) => ({
            ...application,
            key: application._id,
          }))}
          pagination={{
            current: currentPage,
            onChange: (page) => setCurrentPage(page),
          }}
          scroll={{ x: true }}
        />
      )}

      <GroupMembersModal
        visible={modalVisible}
        onClose={closeGroupMembersModal}
        groupMembers={groupMembers}
        groupName={groupName}
      />

      <Modal
        open={editModalVisible}
        title="Change Application Event"
        onCancel={() => setEditModalVisible(false)}
        onOk={handleChangeEvent}
        confirmLoading={confirmLoading}
      >
        {isEventOptionsLoading ? (
          <Skeleton active />
        ) : (
          <Select
            showSearch
            placeholder="Select an event"
            style={{ width: '100%' }}
            value={selectedEventId}
            onChange={(value) => setSelectedEventId(value)}
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option && option.children
                ? option.children.toLowerCase()
                : ''
              ).includes(input.toLowerCase())
            }
          >
            {eventOptions.map((event) => (
              <Select.Option
                value={event._id}
                key={event._id}
              >
                {event.name} - {event.style}
              </Select.Option>
            ))}
          </Select>
        )}
      </Modal>
    </>
  );
};

export default ApplicationTable;